const rawBase = (process.env.REACT_APP_API_BASE_URL || "http://localhost:8000").replace(/\/$/, "");
const rawPrefix = process.env.REACT_APP_API_PREFIX || "/api/v1";
const API_PREFIX = rawPrefix.startsWith("/") ? rawPrefix : `/${rawPrefix}`;

export function getApiBaseUrl() {
  return `${rawBase}${API_PREFIX}`;
}

const STORAGE_ACCESS = "stellar_access_token";
const STORAGE_REFRESH = "stellar_refresh_token";
const STORAGE_USER = "stellar_user";

export function getStoredAccessToken() {
  return localStorage.getItem(STORAGE_ACCESS);
}

export function getStoredUser() {
  const raw = localStorage.getItem(STORAGE_USER);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setStoredAuth(accessToken, refreshToken, user) {
  if (accessToken) localStorage.setItem(STORAGE_ACCESS, accessToken);
  if (refreshToken) localStorage.setItem(STORAGE_REFRESH, refreshToken);
  if (user) localStorage.setItem(STORAGE_USER, JSON.stringify(user));
}

export function clearStoredAuth() {
  localStorage.removeItem(STORAGE_ACCESS);
  localStorage.removeItem(STORAGE_REFRESH);
  localStorage.removeItem(STORAGE_USER);
  localStorage.removeItem("isAuthenticated");
}

function errorMessageFromBody(json, fallback) {
  if (!json || typeof json !== "object") return fallback;
  if (json.detail !== undefined) {
    if (typeof json.detail === "string") return json.detail;
    if (Array.isArray(json.detail)) {
      return json.detail.map((d) => d.msg || JSON.stringify(d)).join(" ");
    }
    return JSON.stringify(json.detail);
  }
  if (json.message) return json.message;
  return fallback;
}

export async function refreshAccessToken() {
  const refresh = localStorage.getItem(STORAGE_REFRESH);
  if (!refresh) throw new Error("No refresh token");

  const res = await fetch(`${getApiBaseUrl()}/login/new-access-token`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refresh }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(errorMessageFromBody(json, "Refresh failed"));
  }
  const tokenData = json.data;
  if (!tokenData?.access_token) {
    throw new Error("Invalid refresh response");
  }
  localStorage.setItem(STORAGE_ACCESS, tokenData.access_token);
  return tokenData.access_token;
}

/**
 * Appel API avec JSON ; ajoute Authorization si un access token est stocké.
 * Sur 401, tente un refresh puis réessaie une fois.
 */
export async function apiRequest(path, options = {}, isRetry = false) {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const url = `${getApiBaseUrl()}${normalized}`;
  const token = getStoredAccessToken();
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(url, { ...options, headers });

  if (res.status === 401 && !isRetry && localStorage.getItem(STORAGE_REFRESH)) {
    try {
      await refreshAccessToken();
      return apiRequest(path, options, true);
    } catch {
      clearStoredAuth();
    }
  }

  return res;
}

export async function loginRequest(email, password) {
  const res = await fetch(`${getApiBaseUrl()}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(errorMessageFromBody(json, "Login failed"));
  }
  const data = json.data;
  if (!data?.access_token || !data?.refresh_token) {
    throw new Error("Invalid login response");
  }
  setStoredAuth(data.access_token, data.refresh_token, data.user);
  return data;
}
